import React, {useEffect, useState} from 'react';
import {runInAction} from 'mobx';

import {useStores} from '../../../../stores/use-stores';

/**
 * Mock user data for Storybook stories
 * Pre-fills userStore with realistic test data for Update/Collect Missing Details scenarios
 */
export const mockUserData = {
  firstName: 'John',
  lastName: 'Smith',
  emailAddress: 'john.smith@example.com',
  dateOfBirth: new Date('1985-03-15'),
  phoneNumber: '0412345678',
  billingAddress: {
    lines: ['123 Main Street'],
    locality: 'Sydney',
    administrative_area: 'NSW',
    postal_code: '2000',
  },
  // isLoggedIn is a getter derived from these two fields (userStore.ts).
  serverSessionId: 'storybook-session',
  isSessionChecked: true,
};

/**
 * Mock signup offers data for Storybook stories
 */
export const mockSignUpOffers = [
  {
    id: '1',
    title: 'Welcome Offer',
    description: 'Get 10% off your first order',
    code: 'WELCOME10',
  },
  {
    id: '2',
    title: 'Birthday Bonus',
    description: 'Special birthday discount',
    code: 'BDAY2025',
  },
];

/**
 * Decorator that injects mock store data for Storybook stories
 *
 * Usage in stories:
 * ```typescript
 * export const UpdateWithData: Story = {
 *   decorators: [WithMockStoreData],
 *   render: () => <Component />
 * };
 * ```
 */
// A decorator must return an element. Returning a component here made React
// render a function as a child ("Functions are not valid as a React child").
export const WithMockStoreData = (Story: React.ComponentType) => (
  <MockStoreData>
    <Story />
  </MockStoreData>
);

const MockStoreData = ({children}: {children: React.ReactNode}) => {
  const {userStore, offersStore} = useStores();
  // The form reads its defaults on mount, so mount it only after injection.
  const [injected, setInjected] = useState(false);

  useEffect(() => {
    // MobX strict mode rejects observable writes outside an action.
    runInAction(() => {
      // Inject mock user data into singleton userStore
      Object.assign(userStore, mockUserData);

      // Inject mock signup offers into singleton offersStore
      // @ts-expect-error - accessing internal property for Storybook mocking
      offersStore._signUpOfferData = mockSignUpOffers;
    });
    setInjected(true);

    // eslint-disable-next-line no-console
    console.log('[Storybook Mock] User data injected:', {
      firstName: userStore.firstName,
      lastName: userStore.lastName,
      email: userStore.emailAddress,
      isLoggedIn: userStore.isLoggedIn,
    });

    // eslint-disable-next-line no-console
    console.log('[Storybook Mock] Signup offers injected:', mockSignUpOffers);

    // Cleanup on unmount
    return () => {
      runInAction(() => {
        // Reset to empty state
        userStore.firstName = '';
        userStore.lastName = '';
        userStore.emailAddress = '';
        userStore.dateOfBirth = undefined;
        userStore.phoneNumber = '';
        userStore.billingAddress = undefined;
        userStore.serverSessionId = '';
        userStore.isSessionChecked = false;
        // @ts-expect-error - Resetting internal property for Storybook cleanup
        offersStore._signUpOfferData = null;
      });
    };
  }, [userStore, offersStore]);

  return injected ? <>{children}</> : null;
};
